// src/CercaFreelancePage.js
// Ricerca freelance con filtri e load more
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { fetchJson, fileUrl } from './api';
import FreelancerPublicView from './FreelancerPublicView';
import FreelancerDetailView from './FreelancerDetailView';
import { useTranslation } from 'react-i18next';

const PAGE_SIZE = 24;

function initials(f) {
  return [(f.nome || ''), (f.cognome || '')]
    .filter(Boolean).map(s => s[0].toUpperCase()).join('') || '?';
}

function resolveAvatar(profile_image_url) {
  if (!profile_image_url) return null;
  if (/^https?:\/\//i.test(profile_image_url)) return profile_image_url;
  const path = profile_image_url.startsWith('/') ? profile_image_url : `/uploads/${profile_image_url}`;
  return fileUrl(path);
}

const S = `
  @import url('https://fonts.googleapis.com/css2?family=Kanit:wght@400;600;700&family=Inter:wght@300;400;500;600&display=swap');

  .cerca-root {
    padding: 28px 32px;
    min-height: 100vh;
    background: #0a0a0f;
    font-family: 'Inter', sans-serif;
    color: #f0ece4;
  }

  .cerca-title {
    font-family: 'Kanit', sans-serif;
    font-size: 24px;
    font-weight: 700;
    letter-spacing: 0.03em;
    margin-bottom: 4px;
  }

  .cerca-sub {
    font-size: 13px;
    color: #8a8a9a;
    margin-bottom: 24px;
  }

  .cerca-filters {
    display: flex;
    gap: 10px;
    margin-bottom: 20px;
    flex-wrap: wrap;
    align-items: center;
  }

  .cerca-input {
    flex: 1;
    min-width: 260px;
    padding: 10px 16px;
    border-radius: 8px;
    border: 1px solid rgba(255,255,255,0.1);
    background: #111118;
    color: #f0ece4;
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    outline: none;
    transition: border-color 0.2s;
  }
  .cerca-input::placeholder { color: #4a4a5a; }
  .cerca-input:focus { border-color: rgba(212,165,80,0.5); }

  .cerca-select {
    padding: 10px 14px;
    border-radius: 8px;
    border: 1px solid rgba(255,255,255,0.1);
    background: #111118;
    color: #f0ece4;
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    outline: none;
    cursor: pointer;
    min-width: 180px;
    transition: border-color 0.2s;
  }
  .cerca-select:focus { border-color: rgba(212,165,80,0.5); }

  .filter-pill {
    padding: 8px 16px;
    border-radius: 20px;
    border: 1px solid rgba(255,255,255,0.1);
    background: transparent;
    color: #8a8a9a;
    font-family: 'Inter', sans-serif;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s;
    white-space: nowrap;
  }
  .filter-pill:hover { border-color: rgba(212,165,80,0.3); color: #d4a550; }
  .filter-pill.active {
    background: rgba(212,165,80,0.12);
    border-color: rgba(212,165,80,0.4);
    color: #d4a550;
  }

  .cerca-meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
  }

  .cerca-count {
    font-size: 12px;
    color: #4a4a5a;
  }

  .cerca-count strong {
    color: #8a8a9a;
  }

  .cerca-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 14px;
  }

  .fl-card {
    background: #111118;
    border: 1px solid rgba(255,255,255,0.07);
    border-radius: 12px;
    padding: 18px;
    cursor: pointer;
    transition: all 0.18s;
  }
  .fl-card:hover {
    border-color: rgba(212,165,80,0.35);
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0,0,0,0.3);
  }

  .fl-card-top {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 12px;
  }

  .fl-avatar {
    width: 46px; height: 46px;
    border-radius: 50%;
    background: linear-gradient(135deg, #d4a550, #8b5e1a);
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Kanit', sans-serif;
    font-size: 16px;
    font-weight: 700;
    color: white;
    flex-shrink: 0;
    overflow: hidden;
  }
  .fl-avatar img { width: 100%; height: 100%; object-fit: cover; border-radius: 50%; }

  .fl-name { font-size: 15px; font-weight: 600; color: #f0ece4; margin-bottom: 3px; }
  .fl-role { font-size: 12px; color: #8a8a9a; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 160px; }

  .fl-status { margin-left: auto; display: flex; align-items: center; gap: 5px; flex-shrink: 0; }
  .status-dot { width: 7px; height: 7px; border-radius: 50%; }
  .status-dot.free { background: #4ade80; box-shadow: 0 0 5px #4ade80; }
  .status-dot.busy { background: #f87171; }
  .status-dot.tentative { background: #a78bfa; box-shadow: 0 0 5px #a78bfa; }
  .status-label { font-size: 10px; font-weight: 600; letter-spacing: 0.5px; text-transform: uppercase; }
  .status-label.free { color: #4ade80; }
  .status-label.busy { color: #f87171; }
  .status-label.tentative { color: #a78bfa; }

  .fl-last-job { font-size: 11px; color: #4a4a5a; margin-bottom: 10px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .fl-last-job span { color: #d4a550; }

  .fl-skills { display: flex; flex-wrap: wrap; gap: 5px; }
  .fl-skill-tag { font-size: 10px; padding: 3px 8px; border-radius: 5px; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.07); color: #8a8a9a; }
  .fl-skill-more { font-size: 10px; color: #4a4a5a; padding: 3px 6px; }

  .load-more-wrap {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    margin-top: 32px;
  }

  .load-more-btn {
    padding: 12px 32px;
    border-radius: 10px;
    border: 1px solid rgba(212,165,80,0.4);
    background: rgba(212,165,80,0.07);
    color: #d4a550;
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.18s;
    letter-spacing: 0.02em;
  }
  .load-more-btn:hover {
    background: rgba(212,165,80,0.14);
    border-color: rgba(212,165,80,0.65);
    transform: translateY(-1px);
    box-shadow: 0 4px 16px rgba(212,165,80,0.12);
  }
  .load-more-btn:disabled {
    opacity: 0.4;
    cursor: default;
    pointer-events: none;
  }

  .load-more-hint {
    font-size: 11px;
    color: #3a3a4a;
  }

  .cerca-empty { text-align: center; padding: 60px 20px; color: #4a4a5a; font-size: 14px; grid-column: 1 / -1; }
  .cerca-loading { text-align: center; padding: 60px; color: #4a4a5a; font-size: 14px; }

  .cerca-back {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    border-radius: 8px;
    border: 1px solid rgba(255,255,255,0.1);
    background: transparent;
    color: #8a8a9a;
    font-family: 'Inter', sans-serif;
    font-size: 13px;
    cursor: pointer;
    margin-bottom: 20px;
    transition: all 0.15s;
  }
  .cerca-back:hover { border-color: rgba(212,165,80,0.4); color: #d4a550; }

  .fl-skeleton {
    background: #111118;
    border: 1px solid rgba(255,255,255,0.05);
    border-radius: 12px;
    padding: 18px;
    height: 130px;
    animation: pulse 1.5s ease-in-out infinite;
  }
  @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.35; } }

  .loading-more {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #4a4a5a;
    font-size: 13px;
    margin-top: 32px;
    justify-content: center;
  }
  .loading-more-dot {
    width: 6px; height: 6px; border-radius: 50%;
    background: #d4a550;
    animation: pulse 1.2s ease-in-out infinite;
  }
  .loading-more-dot:nth-child(2) { animation-delay: 0.2s; }
  .loading-more-dot:nth-child(3) { animation-delay: 0.4s; }
`;

export default function CercaFreelancePage({ isProductionHouse, token }) {
  const { t } = useTranslation();
  const [items, setItems]           = useState([]);
  const [total, setTotal]           = useState(null);
  const [loading, setLoading]       = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError]           = useState(null);
  const [page, setPage]             = useState(1);
  const [allRuoli, setAllRuoli]     = useState([]);
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);

  const [searchParams, setSearchParams] = useSearchParams();

  // Stato filtri inizializzato dalla query string (se presente), così un
  // remount della pagina (es. dopo "indietro" dal profilo di un freelance)
  // riparte dagli stessi filtri invece che da zero.
  const [inputQ, setInputQ]         = useState(() => searchParams.get('q') || '');
  const [q, setQ]                   = useState(() => searchParams.get('q') || '');
  const [ruolo, setRuolo]           = useState(() => searchParams.get('ruolo') || '');
  const [dispFilter, setDispFilter] = useState(() => searchParams.get('disponibile') || '');
  const [cityFilter, setCityFilter] = useState(() => searchParams.get('city') || '');
  const [travelFilter, setTravelFilter] = useState(() => searchParams.get('travel') === 'true');
  const [remoteFilter, setRemoteFilter] = useState(() => searchParams.get('remote') === 'true');

  const debounceRef = useRef(null);
  const isAppend = useRef(false);

  // Debounce testo — 400ms, resetta alla pagina 1
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      isAppend.current = false;
      setQ(inputQ);
      setPage(1);
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [inputQ]);

  // Cambio filtri — resetta alla pagina 1
  useEffect(() => {
    isAppend.current = false;
    setPage(1);
  }, [ruolo, dispFilter, cityFilter, travelFilter, remoteFilter]);

  // Sincronizza i filtri nella query string (replace, non push: non vogliamo
  // una history entry per ogni modifica ai filtri — solo l'apertura di un
  // profilo deve creare un punto a cui tornare con "indietro"). "page" resta
  // esclusa di proposito: il back riparte da pagina 1 con gli stessi filtri.
  useEffect(() => {
    const params = new URLSearchParams();
    if (q)             params.set('q', q);
    if (ruolo)         params.set('ruolo', ruolo);
    if (dispFilter)    params.set('disponibile', dispFilter);
    if (cityFilter)    params.set('city', cityFilter);
    if (travelFilter)  params.set('travel', 'true');
    if (remoteFilter)  params.set('remote', 'true');
    setSearchParams(params, { replace: true });
  }, [q, ruolo, dispFilter, cityFilter, travelFilter, remoteFilter, setSearchParams]);

  const fetchResults = useCallback(async () => {
    const appending = isAppend.current;
    if (appending) setLoadingMore(true);
    else setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (q)          params.set('q', q);
      if (ruolo)      params.set('ruolo', ruolo);
      if (dispFilter) params.set('disponibile', dispFilter);
      if (cityFilter) params.set('city', cityFilter);
      if (travelFilter) params.set('travel', 'true');
      if (remoteFilter) params.set('remote', 'true');
      params.set('page',  String(page));
      params.set('limit', String(PAGE_SIZE));
      if (!q && !ruolo && !dispFilter && !cityFilter && !travelFilter && !remoteFilter && page === 1) params.set('random', 'true');

      const data = await fetchJson(`/freelancers?${params.toString()}`);
      const rows = Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : []);

      setItems(prev => appending ? [...prev, ...rows] : rows);
      setTotal(data?.total ?? null);

      // Popola il select ruoli con i dati reali (una sola volta)
      if (allRuoli.length === 0 && rows.length > 0) {
        const set = new Set();
        rows.forEach(f => (Array.isArray(f.ruoli) ? f.ruoli : []).forEach(r => r && set.add(r)));
        if (set.size > 0) setAllRuoli([...set].sort());
      }
    } catch (e) {
      if (e.message?.includes('invisible')) {
        setError(t('cerca.err_invisible'));
      } else {
        setError(t('cerca.err_load'));
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
      isAppend.current = false;
    }
  }, [q, ruolo, dispFilter, cityFilter, travelFilter, remoteFilter, page]);

  useEffect(() => { fetchResults(); }, [fetchResults]);

  const handleLoadMore = () => {
    isAppend.current = true;
    setPage(p => p + 1);
  };

  const hasMore = total !== null && items.length < total;


  const hasFilters = !!(q || ruolo || dispFilter || cityFilter || travelFilter || remoteFilter);

  return (
    <div className="cerca-root">
      <style>{S}</style>

      <div className="cerca-title">{t('cerca.title')}</div>
      <div className="cerca-sub">
        {hasFilters ? t('cerca.sub_results') : t('cerca.sub_default')}
      </div>

      <div className="cerca-filters">
        <input
          className="cerca-input"
          placeholder={t('cerca.ph_search')}
          value={inputQ}
          onChange={e => setInputQ(e.target.value)}
          autoFocus
        />
        <select className="cerca-select" value={ruolo} onChange={e => setRuolo(e.target.value)}>
          <option value="">{t('cerca.all_roles')}</option>
          {[...(allRuoli.some(r=>r.startsWith('Editor Video'))?['Editor Video']:[]),...allRuoli.filter(r=>!r.startsWith('Editor Video'))].sort((a,b)=>a.localeCompare(b,'it')).map(r=><option key={r} value={r}>{r==='Editor Video'?t('cerca.all_roles_label'):r}</option>)}
        </select>
        <div style={{ display: 'flex', gap: 6 }}>
          {[{ v: '', l: t('cerca.pill_all') }, { v: 'true', l: t('cerca.pill_free') }, { v: 'tentative', l: t('cerca.pill_tentative') }, { v: 'false', l: t('cerca.pill_busy') }].map(({ v, l }) => (
            <button key={v} className={`filter-pill ${dispFilter === v ? 'active' : ''}`} onClick={() => setDispFilter(v)}>{l}</button>
          ))}
        </div>
        <select className="cerca-select" value={cityFilter} onChange={e => setCityFilter(e.target.value)}>
          <option value="">{t('cerca.all_cities')}</option>
          {['Alessandria','Ancona','Aosta','Arezzo','Asti','Bari','Benevento','Bergamo','Bolzano','Bologna','Brescia','Brindisi','Cagliari','Campobasso','Caserta','Catania','Catanzaro','Como','Cosenza','Cuneo','Estero','Ferrara','Firenze','Foggia','Frosinone','Genova',"L'Aquila",'La Spezia','Latina','Lecce','Livorno','Messina','Milano','Modena','Napoli','Novara','Nuoro','Padova','Palermo','Parma','Pescara','Perugia','Pisa','Potenza','Ravenna','Reggio Calabria','Reggio Emilia','Rieti','Rimini','Roma','Salerno','Sassari','Savona','Siena','Taranto','Teramo','Torino','Trento','Treviso','Trieste','Udine','Varese','Venezia','Verona','Vicenza','Viterbo'].map(c => (
            <option key={c} value={c}>{c === 'Estero' ? t('cerca.city_abroad') : c}</option>
          ))}
        </select>
        <div style={{ display: 'flex', gap: 6 }}>
          <button className={`filter-pill ${travelFilter ? 'active' : ''}`} onClick={() => setTravelFilter(v => !v)}>{t('cerca.pill_travel')}</button>
          <button className={`filter-pill ${remoteFilter ? 'active' : ''}`} onClick={() => setRemoteFilter(v => !v)}>{t('cerca.pill_remote')}</button>
        </div>
      </div>

      <div className="cerca-meta">
        <span className="cerca-count">
          {loading
            ? '...'
            : total !== null
              ? <>{t('cerca.count_shown')} <strong>{items.length}</strong> {t('cerca.count_of')} <strong>{total}</strong> {t('cerca.count_profiles')}</>
              : <><strong>{items.length}</strong> {t('cerca.count_profiles')}</>
          }
        </span>
      </div>

      {error ? (
        <div className="cerca-loading" style={{ color: '#f87171' }}>{error}</div>
      ) : (
        <div className="cerca-grid">
          {loading
            ? Array.from({ length: 12 }).map((_, i) => <div key={i} className="fl-skeleton" />)
            : items.length === 0
              ? <div className="cerca-empty">{t('cerca.empty')}</div>
              : items.map(f => {
                  const ruoli  = Array.isArray(f.ruoli) ? f.ruoli : (f.ruolo ? [f.ruolo] : []);
                  const skills = Array.isArray(f.skills) ? f.skills : [];
                  const lastJob = f.azienda && f.produzione ? `${f.azienda} · ${f.produzione}` : f.azienda || f.produzione || null;
                  const avatar = resolveAvatar(f.profile_image_url);
                  const busy   = !!f.is_occupied;
                  const tentative = !busy && !!f.is_tentative;
                  const statusClass = busy ? 'busy' : tentative ? 'tentative' : 'free';
                  const statusLabel = busy ? t('cerca.status_busy') : tentative ? t('cerca.status_tentative') : t('cerca.status_free');
                  return (
                    <div key={f.id} className="fl-card" onClick={() => { navigate(`/freelancer/${f.freelancer_id ?? f.id}`); }}>
                      <div className="fl-card-top">
                        <div className="fl-avatar">
                          {avatar
                            ? <img src={avatar} alt="" onError={e => { e.target.style.display = 'none'; }} />
                            : initials(f)
                          }
                        </div>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div className="fl-name">{`${f.nome || ''} ${f.cognome || ''}`.trim() || '—'}</div>
                          <div className="fl-role">{ruoli.slice(0, 2).join(' · ') || '—'}</div>
                          {Array.isArray(f.cities) && f.cities.length > 0 && (
                            <div style={{ fontSize:10, color:'#4a4a5a', marginTop:2 }}>📍 {f.cities.slice(0,2).join(', ')}</div>
                          )}
                        </div>
                        <div className="fl-status">
                          <span className={`status-dot ${statusClass}`} />
                          <span className={`status-label ${statusClass}`}>{statusLabel}</span>
                        </div>
                      </div>
                      {lastJob && <div className="fl-last-job">{t('cerca.last_job')}<span>{lastJob}</span></div>}
                      {skills.length > 0 && (
                        <div className="fl-skills">
                          {skills.slice(0, 4).map(s => <span key={s} className="fl-skill-tag">{s}</span>)}
                          {skills.length > 4 && <span className="fl-skill-more">+{skills.length - 4}</span>}
                        </div>
                      )}
                    </div>
                  );
                })
          }
        </div>
      )}

      {/* Load more */}
      {!loading && loadingMore && (
        <div className="loading-more">
          <div className="loading-more-dot" />
          <div className="loading-more-dot" />
          <div className="loading-more-dot" />
        </div>
      )}

      {!loading && !loadingMore && hasMore && (
        <div className="load-more-wrap">
          <button className="load-more-btn" onClick={handleLoadMore}>
            {t('cerca.load_more')}
          </button>
          <span className="load-more-hint">
            {total - items.length} {t('cerca.load_more_hint')}
          </span>
        </div>
      )}
    </div>
  );
}
